import "../../../../../../theme/MemoriaParejas.css";
import estiloJuegos from "../../../../../../theme/Juegos.module.css";

import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

import PopoverNeuronasParejas from "./componentes/PopoverNeuronasParejas";
import TableroParejas from "./componentes/TableroParejas";
import { aSegundos, TiempoSubSec } from "./componentes/TemporizadorSubSegundo";
import { test_general } from "../../../../../../datos/DatosTests";
import { sumaNeuronas } from "../../../../../../redux/auth/reducerAuth";
import { changePuntaje, terminadoJuego } from "../../../../../../redux/juegoSimultaneo/reducerJuegoSimultaneo";
import { useAppSelector, useAppDispatch } from "../../../../../../redux/store";
import { useJuegos } from "../../../../../../repositorio/controlador/useJuegos";
import Loading from "../../../../../components/Loading";

interface Nivel {
  cantidad: number;
  tiempo: number;        // centésimas de segundo (cs)
  multiplicador: number;
}

/* ============================================================
   === PARÁMETROS DE PUNTAJE (EDITABLES) ======================
   Objetivo: total de sesión 0..TOTAL_MAX (por defecto 300).
   PESOS_NIVEL debe tener la misma longitud que datosNiveles
   y su suma idealmente ser TOTAL_MAX.
   ------------------------------------------------------------ */
const TOTAL_MAX = 300;

// Reparte el "pastel" total entre niveles (suman 300):
// Variante equitativa: [60, 60, 60, 60, 60]
// Variante que premia avanzar: [45, 55, 60, 70, 70]
const PESOS_NIVEL = [45, 55, 60, 70, 70];

// Penalización por error = porcentaje del peso de ese nivel
// (0.08 = 8% por error. En nivel de peso 70, cada error resta ~5.6)
const ERROR_PENALTY_FRAC = 0.08;

// Puntaje de TORNEO: reescala el acumulado 0..TOTAL_MAX a un rango
// equiparable al de otros juegos del torneo (Sumas llega hasta ~9000 en una
// corrida perfecta), para que ningun juego pese mucho mas que otro en el
// total del jugador. 300 x 30 = 9000.
const ESCALA_TORNEO = 30;
/* ============================================================ */

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

const JuegoDeMemoriaParejas = () => {
  const datosNiveles: Nivel[] = [
    { cantidad: 4,  tiempo: 300,  multiplicador: 4 },
    { cantidad: 6,  tiempo: 500,  multiplicador: 4 },
    { cantidad: 8,  tiempo: 600,  multiplicador: 4 },
    { cantidad: 12, tiempo: 800,  multiplicador: 5 },
    { cantidad: 16, tiempo: 1200, multiplicador: 5 },
  ];

  // Redux / router
  const esJuego     = useAppSelector((s) => s.activity.id === test_general);
  const userId      = useAppSelector((s) => s.auth.userId);
  const idActividad = useAppSelector((s) => s.activity.id);
  const dispatch    = useAppDispatch();
  const navigate    = useNavigate();

  const { guardarPuntajeParejas } = useJuegos();

  // Estado UI
  const [puntajeMostrado, setPuntajeMostrado] = useState<number>(0); // lo que ve el usuario en el popover
  const [subEtapa, setSubEtapa] = useState<number>(3);
  const [etapa, setEtapa] = useState<number>(0);
  const [nivel, setNivel] = useState<number>(0);
  const [cargado, setCargado] = useState<boolean[]>(Array(datosNiveles.length).fill(false));
  const [tiempo, setTiempo] = useState<number>(datosNiveles[0].tiempo);
  const [totalTiempo, setTotalTiempo] = useState<number>(0);  // tiempo transcurrido del NIVEL actual (cs)
  const [intentos, setIntentos] = useState<number>(0);        // errores ACUMULADOS (solo cuando hay victoria)

  // Refs (estado persistente/control de acreditación)
  const finalRef        = useRef<boolean>(false);    // si ganó el ÚLTIMO nivel (no es necesario para el nuevo esquema, pero lo dejamos)
  const awardedRef      = useRef<boolean>(false);    // evita doble cálculo por nivel
  const intentosRef     = useRef<number>(0);
  const nivelRef        = useRef<number>(0);
  const tiempoMaxRef    = useRef<number>(0);         // tiempo máximo del nivel (cs)
  // Instante real (Date.now) en que arranca el cronometro del nivel. El tiempo
  // usado se mide directo contra el reloj real al cerrar el nivel, en vez de
  // ir acumulando deltas paso a paso (ese mecanismo nunca llegaba a sumar
  // nada: el puntaje de velocidad de Parejas siempre daba el maximo sin
  // importar que tan rapido jugara la persona).
  const inicioNivelRef  = useRef<number>(0);

  // NUEVO: acumulado de la sesión y control de acreditación incremental
  const acumScoreRef    = useRef<number>(0);         // puntaje ACUMULADO 0..TOTAL_MAX
  const acreditadoRef   = useRef<number>(0);         // cuánto ya se acreditó a neuronas (para abonar solo la diferencia)
  const erroresNivelRef = useRef<number>(0);         // errores del NIVEL actual

  useEffect(() => { intentosRef.current    = intentos; }, [intentos]);
  useEffect(() => { nivelRef.current       = nivel; }, [nivel]);

  /* ============================================================
     === CÁLCULO DE PUNTAJE POR NIVEL ===========================
     Regla: el nivel i aporta 0..PESOS_NIVEL[i] según velocidad,
     y se resta ERROR_PENALTY_FRAC * PESOS_NIVEL[i] por cada error.
     No hay bono final aparte: los pesos ya suman TOTAL_MAX.
     ============================================================ */
  const calcScoreNivel = (elapsedCs: number, erroresNivel: number): number => {
    const idx = clamp(nivelRef.current, 0, PESOS_NIVEL.length - 1);
    const pesoNivel = PESOS_NIVEL[idx];
    const tiempoMax = Math.max(1, tiempoMaxRef.current); // evita /0
    const speedPct  = clamp(1 - elapsedCs / tiempoMax, 0, 1); // 0..1 (1 = súper rápido)

    // Sin redondear aqui: se conserva toda la precision de las centesimas
    // de segundo medidas, para que el puntaje final tenga suficiente
    // resolucion y 200 jugadores casi nunca empaten exacto. Se redondea
    // una sola vez, al final de la sesion.
    const scoreVelocidad = speedPct * pesoNivel;                 // 0..pesoNivel
    const penaltyErrores = pesoNivel * ERROR_PENALTY_FRAC * Math.max(0, erroresNivel || 0);

    // Puntaje del nivel limitado a 0..pesoNivel
    return clamp(scoreVelocidad - penaltyErrores, 0, pesoNivel);
  };

  const resetAwardFlag = () => { awardedRef.current = false; };

  // Navegación
  const Salida = () => {
    if (idActividad === "juego_linea") {
      dispatch(changePuntaje({ verPuntaje: true }));
    } else {
      navigate("/my/juegos/Memoria");
    }
  };

  // Flujo de juego
  const Reiniciar = () => {
    setCargado(Array(datosNiveles.length).fill(false));
    setNivel(0);
    setEtapa(0);
    setSubEtapa(3);
    setTotalTiempo(0);
    setIntentos(0);
    setTiempo(datosNiveles[0].tiempo);

    finalRef.current = false;
    resetAwardFlag();

    // RESET acumuladores
    acumScoreRef.current  = 0;
    acreditadoRef.current = 0;
    erroresNivelRef.current = 0;

    setPuntajeMostrado(0);
    tiempoMaxRef.current = 0;
    inicioNivelRef.current = 0;
  };

  const IniciarJuego = () => {
    setEtapa(1);
    setSubEtapa(0);
    finalRef.current = false;
    resetAwardFlag();
    erroresNivelRef.current = 0;
    tiempoMaxRef.current = 0;
    inicioNivelRef.current = 0;
  };

  const getEstadoCartel = () => {
    if (etapa === 0 || !cargado[nivel] || etapa > 1) return "ocultoAbajo";
    if (subEtapa === 1) return "ocultoArriba";
    return "";
  };

  const ManejarFinalizoConteo = () => {
    switch (etapa) {
      case 1: {
        // pasa a etapa de juego (cronómetro corre)
        const maxCs = datosNiveles[nivel].tiempo * datosNiveles[nivel].multiplicador;
        setTiempo(maxCs);
        tiempoMaxRef.current = maxCs;         // guarda tiempo máximo del nivel
        erroresNivelRef.current = 0;          // resetea errores del nivel actual
        setSubEtapa(2);
        setTimeout(() => setEtapa(2), 1200);
        inicioNivelRef.current = Date.now();  // arranca el reloj real del nivel
        break;
      }
      case 2:
        // termina nivel (por tiempo o por victoria)
        setEtapa(3);
        break;
    }
  };

  const IrSiguienteNivel = () => {
    resetAwardFlag();
    if (nivel + 1 < datosNiveles.length) {
      setNivel((prev) => prev + 1);
      setTiempo(datosNiveles[nivel + 1].tiempo);
      tiempoMaxRef.current = 0;
      setEtapa(1);
      setSubEtapa(0);
      setTotalTiempo(0);
      erroresNivelRef.current = 0;
      finalRef.current = false;
      inicioNivelRef.current = 0;
    } else {
      Reiniciar();
    }
  };

  const Victoria = (intentosNivel: number) => {
    // Guarda los errores de ESTE nivel para el cálculo de puntaje por nivel
    erroresNivelRef.current = intentosNivel;

    // Conserva tu acumulado global de errores si lo usas en estadísticas
    setIntentos((prev) => prev + intentosNivel);

    // (opcional) marca si fue el último nivel
    if (nivel >= datosNiveles.length - 1) {
      finalRef.current = true;
    }
    setEtapa(3);
    setTimeout(() => { if (etapa === 3) setSubEtapa(0); }, 800);
  };

  // Efectos (no acreditan salvo el de cierre)
  useEffect(() => {
    if (etapa === 1 && cargado[nivel]) {
      finalRef.current = false;   // al iniciar nivel, nunca hay bono especial
      setTimeout(() => setSubEtapa(1), 1200);
    }
  }, [cargado, etapa, nivel]);

  // === CIERRE DE NIVEL (cálculo único) ===
  useEffect(() => {
    if (etapa !== 3) return;
    if (awardedRef.current) return;

    awardedRef.current = true;

    // Tiempo real usado en el nivel, medido contra el reloj (Date.now), no
    // acumulado tick a tick — asi no importa si hubo cortes o el navegador
    // se atraso en algun disparo del cronometro.
    const elapsedCs = clamp(Math.round((Date.now() - inicioNivelRef.current) / 10), 0, tiempoMaxRef.current);
    setTotalTiempo(elapsedCs);
    const erroresDeNivel  = erroresNivelRef.current || 0;

    // 1) Puntaje de ESTE nivel (0..pesoNivel)
    const scoreNivel = calcScoreNivel(elapsedCs, erroresDeNivel);

    // 2) Acumula a la sesión (0..TOTAL_MAX)
    const nuevoAcum = clamp(acumScoreRef.current + scoreNivel, 0, TOTAL_MAX);
    acumScoreRef.current = nuevoAcum;

    // 3) Valor que se muestra al usuario:
    const enLinea = idActividad === "juego_linea";
    const mostrado = enLinea
      ? Math.max(1, Math.round(nuevoAcum * ESCALA_TORNEO)) // 0..9000 para torneo, equiparable a Sumas
      : Math.round(nuevoAcum);                             // 0..300 para modo normal

    setPuntajeMostrado(mostrado);

    const tiempoSeg = aSegundos(elapsedCs);

    // 4) Acreditación INCREMENTAL:
    //    abonamos solo la DIFERENCIA respecto a lo ya acreditado
    //    para que el saldo final coincida con el acumulado mostrado,
    //    evitando doble conteo entre niveles.
    if (!enLinea) {
      const delta = Math.max(0, Math.round(nuevoAcum - acreditadoRef.current));
      if (delta > 0) {
        dispatch(sumaNeuronas({ cantidad: delta }));
        acreditadoRef.current = nuevoAcum;

        // Guarda un snapshot (acumulado actual y delta abonado)
        guardarPuntajeParejas(userId, {
          puntajeRaw: nuevoAcum,    // acumulado 0..300
          neuronas: delta,          // lo que se abonó en esta pantalla
          tiempo: tiempoSeg,        // tiempo del nivel actual (puedes cambiar a acumulado si quieres)
          intentos: intentosRef.current, // errores acumulados con victorias
        });
      }
    } else {
      // En juego en línea, informamos el progreso normalizado 0..100
      dispatch(terminadoJuego({
        ultimoResultado: mostrado,
        tiempo: tiempoSeg,
        finalizado: (nivelRef.current >= datosNiveles.length - 1) && finalRef.current,
      }));
    }
  }, [etapa, idActividad, guardarPuntajeParejas, userId, dispatch]);

  // Render
  return (
    <div id="page" style={idActividad === "juego_linea" ? { height: "100%" } : {}}>
      {etapa > 0 && !cargado[nivel] && <Loading isOpen={true} />}

      {(idActividad !== "juego_linea" || etapa === 0) && (
        <div className={etapa === 0 ? estiloJuegos.toolbarParejas : estiloJuegos.toolbar_default}>
          {esJuego && <Link className={estiloJuegos.backJuegos} to=".." relative="path" />}
          <h1 className={`title ${estiloJuegos.tituloJuegos}`} style={etapa !== 1 ? { fontSize: "2.37svh" } : {}}>
            PAREJAS
          </h1>
        </div>
      )}

      <div id="content_default" style={etapa === 3 ? { position: "relative" } : {}}>
        {etapa === 0 && (
          <>
            <div className={estiloJuegos.contenedorIconoParejas}>
              <img src="/assets/componentes/Juegos/Parejas.svg" className={estiloJuegos.icono} />
            </div>
            <div className={estiloJuegos.primeraPantalla}>
              <div className={estiloJuegos.descripcionJuego}>
                ¡Mira bien las cartas durante unos segundos y trata de recordar dónde está cada pareja!
                Luego, voltea las cartas de a dos para encontrar las que son iguales.
              </div>
              <button className={estiloJuegos.buttonEmpezar} onClick={IniciarJuego}>EMPEZAR</button>
              {esJuego && (
                <button id="button_bordes_default" onClick={() => navigate("/my/juegos/ranking")}>
                  RANKING
                </button>
              )}
            </div>
          </>
        )}

        {etapa > 0 && (
          <div className="topInfoContainer" style={{ margin: "1rem 0.5rem 0 0.5rem" }}>
            <img src="/assets/icon/relojBlanco2.svg" alt="⏱" />
            {!cargado[nivel] && "00:00.0"}
            {cargado[nivel] && etapa > 0 && (
              <TiempoSubSec
                inicial={tiempo}
                contar={subEtapa > 0 && etapa < 3}
                ascendente={false}
                conteoTermino={ManejarFinalizoConteo}
                tiempoActual={setTiempo}
                reiniciar={etapa}
              />
            )}
          </div>
        )}

        <div className={`cartelPreSubEtapa ${getEstadoCartel()}`}>
          {subEtapa < 2 ? "¡Memoriza las cartas!" : "¡Encuentra las parejas!"}
        </div>

        {datosNiveles.map((datos, indice) => (
          <TableroParejas
            key={indice}
            cantidad={datos.cantidad}
            visible={etapa > 0 && nivel === indice}
            estado={subEtapa}
            onLoad={() => setCargado((prev) => { const n = [...prev]; n[indice] = true; return n; })}
            onWin={Victoria}
          />
        ))}

        <div className={etapa === 3 ? "" : "desaparecer"}>
          <div className="opaqueFinal" />
          <PopoverNeuronasParejas
            tiempoTotal={totalTiempo}
            tiempo={tiempo}
            intentos={intentos}
            nivel={nivel}
            nivelFinal={nivel >= datosNiveles.length - 1}
            estado={tiempo > 0}
            // ahora muestra ACUMULADO
            puntaje={puntajeMostrado}
            enLinea={idActividad === "juego_linea"}
            onContinuar={IrSiguienteNivel}
            onReiniciar={Reiniciar}
            onSalir={Salida}
          />
        </div>
      </div>
    </div>
  );
};

export default JuegoDeMemoriaParejas;
